import { getTelegramInitData } from "../lib/telegram.js";
import type {
  CartResponseDTO,
  CatalogCategoriesResponse,
  CatalogSearchResponse,
  CheckoutDraftDTO,
  CheckoutErrorResponse,
  CheckoutSubmitRequest,
  MiniAppErrorResponse,
  MiniAppManagerSystemStatus,
  MiniAppSessionResponse,
  ProductDetailResponse,
  StorefrontHomeResponse,
} from "./types.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";

function buildUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return API_BASE ? `${API_BASE}${normalized}` : normalized;
}

const BUILD_ID = import.meta.env.VITE_BUILD_ID?.trim() ?? "";

function authHeaders(): HeadersInit {
  const initData = getTelegramInitData();
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (initData) {
    headers.Authorization = `tma ${initData}`;
  }
  if (BUILD_ID) {
    headers["x-miniapp-build-id"] = BUILD_ID;
  }
  return headers;
}

export function getFrontendBuildId(): string {
  return BUILD_ID;
}

export function getApiBaseForDebug(): string {
  return API_BASE || "";
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    throw new Error(`Empty response (${response.status})`);
  }
  return JSON.parse(text) as T;
}

export async function fetchSession(): Promise<MiniAppSessionResponse> {
  const url = buildUrl("/api/mini-app/me");
  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: authHeaders(),
    });
  } catch (err) {
    const hint =
      err instanceof TypeError
        ? ` (network/CORS — endpoint ${url})`
        : "";
    throw new Error(
      `Failed to fetch${hint}: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Session failed (${response.status})`);
  }

  return parseJson<MiniAppSessionResponse>(response);
}

export async function fetchStorefrontHome(): Promise<StorefrontHomeResponse> {
  const response = await fetch(buildUrl("/api/mini-app/home"), {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Home failed (${response.status})`);
  }
  return parseJson<StorefrontHomeResponse>(response);
}

export interface FetchCatalogParams {
  q?: string;
  limit?: number;
  cursor?: string | null;
  category?: string;
  collection?: string;
}

export async function fetchCatalogCategories(): Promise<CatalogCategoriesResponse> {
  const response = await fetch(buildUrl("/api/mini-app/catalog/categories"), {
    method: "GET",
    headers: authHeaders(),
  });

  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Categories failed (${response.status})`);
  }

  return parseJson<CatalogCategoriesResponse>(response);
}

export async function fetchCatalog(
  params: FetchCatalogParams = {},
): Promise<CatalogSearchResponse> {
  const searchParams = new URLSearchParams();
  if (params.q?.trim()) {
    searchParams.set("q", params.q.trim());
  }
  if (params.limit != null) {
    searchParams.set("limit", String(params.limit));
  }
  if (params.cursor) {
    searchParams.set("cursor", params.cursor);
  }
  if (params.category?.trim()) {
    searchParams.set("category", params.category.trim());
  }
  if (params.collection?.trim()) {
    searchParams.set("collection", params.collection.trim());
  }

  const query = searchParams.toString();
  const path = query ? `/api/mini-app/catalog?${query}` : "/api/mini-app/catalog";

  const response = await fetch(buildUrl(path), {
    method: "GET",
    headers: authHeaders(),
  });

  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Catalog failed (${response.status})`);
  }

  return parseJson<CatalogSearchResponse>(response);
}

export async function fetchProductDetail(productId: string): Promise<ProductDetailResponse> {
  const response = await fetch(
    buildUrl(`/api/mini-app/catalog/${encodeURIComponent(productId)}`),
    {
      method: "GET",
      headers: authHeaders(),
    },
  );

  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Product failed (${response.status})`);
  }

  return parseJson<ProductDetailResponse>(response);
}

async function handleCartResponse(response: Response): Promise<CartResponseDTO> {
  if (!response.ok) {
    const body = await parseJson<MiniAppErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    throw new Error(body.error ?? `Cart failed (${response.status})`);
  }
  return parseJson<CartResponseDTO>(response);
}

export async function fetchCart(): Promise<CartResponseDTO> {
  const response = await fetch(buildUrl("/api/mini-app/cart"), {
    method: "GET",
    headers: authHeaders(),
  });
  return handleCartResponse(response);
}

export async function addCartItem(
  productId: string,
  qty = 1,
): Promise<CartResponseDTO> {
  const response = await fetch(buildUrl("/api/mini-app/cart/items"), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ product_id: productId, qty }),
  });
  return handleCartResponse(response);
}

export async function updateCartItemQty(
  productId: string,
  qty: number,
): Promise<CartResponseDTO> {
  const response = await fetch(
    buildUrl(`/api/mini-app/cart/items/${encodeURIComponent(productId)}`),
    {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ qty }),
    },
  );
  return handleCartResponse(response);
}

export async function removeCartItem(productId: string): Promise<CartResponseDTO> {
  const response = await fetch(
    buildUrl(`/api/mini-app/cart/items/${encodeURIComponent(productId)}`),
    {
      method: "DELETE",
      headers: authHeaders(),
    },
  );
  return handleCartResponse(response);
}

export async function clearCart(): Promise<CartResponseDTO> {
  const response = await fetch(buildUrl("/api/mini-app/cart/clear"), {
    method: "POST",
    headers: authHeaders(),
  });
  return handleCartResponse(response);
}

async function handleCheckoutResponse(response: Response): Promise<CheckoutDraftDTO> {
  if (!response.ok) {
    const body = await parseJson<CheckoutErrorResponse>(response).catch(() => ({
      ok: false as const,
      error: `HTTP ${response.status}`,
    }));
    const fieldErrors =
      "field_errors" in body && body.field_errors ? body.field_errors : undefined;
    const fieldHint =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? ` (${Object.entries(fieldErrors)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")})`
        : "";
    if (body.error === "checkout_price_confirmation_required") {
      throw new Error("checkout_price_confirmation_required");
    }
    throw new Error((body.error ?? `Checkout failed (${response.status})`) + fieldHint);
  }
  return parseJson<CheckoutDraftDTO>(response);
}

export async function trackMiniAppEvent(
  eventType: string,
  properties: Record<string, unknown> = {},
): Promise<void> {
  try {
    await fetch(buildUrl("/api/mini-app/events"), {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ event_type: eventType, properties }),
    });
  } catch {
    /* non-blocking telemetry */
  }
}

export async function submitCheckoutDraft(
  body: CheckoutSubmitRequest,
): Promise<CheckoutDraftDTO> {
  const response = await fetch(buildUrl("/api/mini-app/checkout/draft"), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleCheckoutResponse(response);
}

export async function fetchCheckoutDraft(): Promise<CheckoutDraftDTO> {
  const response = await fetch(buildUrl("/api/mini-app/checkout/draft"), {
    method: "GET",
    headers: authHeaders(),
  });
  return handleCheckoutResponse(response);
}

/** MINIAPP-075 — staging catalog snapshot status (R2/demo metadata) */
export async function fetchCatalogSnapshotMeta(): Promise<Record<string, unknown>> {
  const response = await fetch(buildUrl("/api/mini-app/catalog/snapshot-meta"), {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Catalog snapshot-meta failed (${response.status})`);
  }
  return parseJson<Record<string, unknown>>(response);
}

export async function fetchManagerSystemStatus(): Promise<MiniAppManagerSystemStatus> {
  const response = await fetch(buildUrl("/api/mini-app/manager/system-status"), {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Manager status failed (${response.status})`);
  }
  return parseJson<MiniAppManagerSystemStatus>(response);
}

export async function triggerManagerPricingDashboard(): Promise<{ ok: true; delivery_channel?: string }> {
  const response = await fetch(buildUrl("/api/mini-app/manager/pricing-dashboard"), {
    method: "POST",
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Pricing dashboard failed (${response.status})`);
  }
  return parseJson<{ ok: true; delivery_channel?: string }>(response);
}
